import { Component } from '@theme/component';

/**
 * <logo-morph> — scroll-linked brand logo morph (v2).
 *
 * Open state:  AWAYFROM ( icon marquee ← ) BJECT   — large, single header row
 * Closing:     icon strip width collapses, parens meet, whole lockup shrinks
 *              to the tight compact wordmark (client ref: menu-kucuk-hal).
 *
 * Mechanics: passive scroll maps scrollY/[data-scroll-distance] to
 * --logo-progress (0..1). ALL geometry flows from CSS (--logo-h interpolation
 * + natural flex convergence) — no measurements needed. The icon marquee is a
 * pure CSS animation; its duration comes from [data-icon-interval] × icon count.
 *
 * Self-healing: theme section-render/morph passes (e.g. cart-icon refreshing
 * the header on load) reset client-added classes/inline styles to server HTML;
 * a MutationObserver re-establishes state whenever that happens.
 *
 * Fallbacks: no-JS and mobile stay compact via CSS defaults; reduced-motion
 * snaps between the two states and stops the marquee (CSS).
 *
 * Owned by feature/logo-scroll-animation. No core theme files touched.
 */
class LogoMorph extends Component {
  #distance = 350;
  #ticking = false;
  #healing = false;
  #mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  #headerObserver = new MutationObserver(() => this.#syncHeaderState());

  #selfObserver = new MutationObserver(() => {
    if (this.#healing) return;
    if (this.classList.contains('is-ready')) return;
    this.#healing = true;
    queueMicrotask(() => {
      this.#setup();
      this.#healing = false;
    });
  });

  connectedCallback() {
    super.connectedCallback();

    if (!this.classList.contains('logo-morph--expandable')) return;

    this.#distance = parseInt(this.dataset.scrollDistance, 10) || 350;
    this.#setup();
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#selfObserver.observe(this, { attributes: true, attributeFilter: ['class', 'style'] });

    // header'ın sticky-state'i dışarıdan sıfırlanırsa (morph süpürmesi /
    // vendor gözlemcisi) anında yeniden hesapla — fark yoksa yazmayız, döngü olmaz
    const header = document.querySelector('#header-component');
    if (header) {
      this.#headerObserver.observe(header, { attributes: true, attributeFilter: ['data-sticky-state'] });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.#onScroll);
    this.#selfObserver.disconnect();
    this.#headerObserver.disconnect();
  }

  /** Section Rendering morph sonrası Component API kancası. */
  updatedCallback() {
    super.updatedCallback?.();
    this.#setup();
  }

  #setup = () => {
    // sticky announcement offset'i: vendor --header-group-height,
    // #header-group display:contents olduğundan 0 ölçüyor — kendimiz ölçeriz
    const ann = document.querySelector('.announcement-bar');
    if (ann) document.body.style.setProperty('--afo-announcement-h', `${ann.offsetHeight}px`);

    // marquee süresi: ikon başına interval × ikon sayısı (tek set)
    const interval = parseInt(this.dataset.iconInterval, 10) || 2500;
    const iconCount = this.querySelectorAll('.logo-morph__icon').length / 2 || 10;
    this.style.setProperty('--logo-marquee-duration', `${((interval * iconCount) / 1000).toFixed(1)}s`);
    this.classList.add('is-ready');
    this.#applyProgress();
    this.#syncHeaderState();
  };

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      if (!this.classList.contains('is-ready')) this.#setup();
      this.#applyProgress();
      this.#ticking = false;
    });
  };

  #applyProgress = () => {
    let progress = Math.min(1, Math.max(0, window.scrollY / this.#distance));
    if (this.#mqReduced.matches) progress = progress > 0.5 ? 1 : 0;
    this.style.setProperty('--logo-progress', progress.toFixed(4));
    this.toggleAttribute('data-compact', progress >= 1);

    this.#syncHeaderState();
  };

  /**
   * Şeffaf→solid geçiş sensörü: sticky-announcement offset'i vendor'ın
   * IntersectionObserver hilesini (top:-1px) kör ettiği için
   * data-sticky-state'i scroll pozisyonundan biz besliyoruz.
   * Section-render süpürmesi (ör. cart-icon'un header tazelemesi) bu
   * attribute'u da sıfırlıyor — o yüzden hem her scroll'da, hem setup'ta,
   * hem de header-attribute gözlemcisinden çağrılır. (Video analizindeki
   * "scroll sabitken büyüme" bug'ının kök nedeni buydu.)
   */
  #syncHeaderState = () => {
    const header = document.querySelector('#header-component');
    if (header?.hasAttribute('transparent')) {
      const state = window.scrollY > 8 ? 'active' : 'inactive';
      if (header.dataset.stickyState !== state) header.dataset.stickyState = state;
    }
  };
}

if (!customElements.get('logo-morph')) {
  customElements.define('logo-morph', LogoMorph);
}
